import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { PartnerLiquidationsService } from '../services/partner-liquidations';
import { PartnerDocumentItem, PartnerLiquidationDetail, PartnerLiquidationRow, PartnerLiquidationRowDiscount } from '../../../shared/models/partner-liquidation-detail';
import { AlertService } from '../../../core/ui/alert';
import { LoaderService } from '../../../core/ui/loader';
import { Storage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from '@angular/fire/storage';
import { AuthService } from '../../../core/authentication/auth';
import { convertFirestoreTimestampToDate } from '../../../shared/utils/date-converter';

type PartnerDocumentDraft = PartnerDocumentItem & { file?: File; storagePath?: string; previewUrl?: string };
type InvoiceRowDraft = {
    busId              : string;
    busName?           : string;
    groupId?           : string;
    group?             : string;
    groupType?         : 'partner' | 'entity' | 'route' | 'bus';
    date               : string;
    passagesAmount     : number;
    discounts          : PartnerLiquidationRowDiscount[];
    isrAmount          : number;
    retentionIVAAmount : number;
    concept?           : string;
    totalToInvoice     : number;
    netAmount          : number;
    depositAmount      : number;
    invoice            : PartnerDocumentDraft;
    deposit            : PartnerDocumentDraft;
};

@Component({
    selector: 'app-liquidation-detail',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        FormsModule,
        MatCardModule,
        MatTableModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatDividerModule,
        MatTooltipModule,
        MatDatepickerModule,
        MatNativeDateModule
    ],
    templateUrl: './liquidation-detail.html',
    styleUrl: './liquidation-detail.css'
})
export class LiquidationDetail implements OnInit, OnDestroy {
    liquidationId = '';
    detail: PartnerLiquidationDetail | null = null;

    invoiceRows: InvoiceRowDraft[] = [];
    invoiceRowsToBill: Array<{ row: InvoiceRowDraft; index: number }> = [];
    depositRowsToBill: Array<{ row: InvoiceRowDraft; index: number }> = [];

    totalPassages = 0;
    totalDiscounts = 0;
    netAmount = 0;
    totalToDeposit = 0;
    totalToInvoice = 0;
    paysISR = false;
    statusText = '';
    readonly today = new Date();

    uploading = false;
    uploadTarget: 'invoices' | 'deposits' | null = null;
    uploadProgress = 0;
    uploadMessage = '';

    private alive = true;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private partnerLiquidationsService: PartnerLiquidationsService,
        private alertService: AlertService,
        private loaderService: LoaderService,
        private storage: Storage,
        private authService: AuthService
    ) {}

    get canEdit(): boolean {
        const status = this.detail?.status;
        return status === 'PENDING_UPLOAD' || status === 'REJECTED';
    }

    ngOnInit(): void {
        const idFromRoute = this.route.snapshot.paramMap.get('id');
        if(idFromRoute) {
            this.liquidationId = idFromRoute;
            this.loadDetail();
        }
    }

    ngOnDestroy(): void {
        this.alive = false;
    }

    trackInvoiceRowByIndex(_index: number, item: { row: InvoiceRowDraft; index: number }): number {
        return item.index;
    }

    private rebuildInvoiceRowsToBill(): void {
        this.invoiceRowsToBill = (this.invoiceRows || [])
            .map((row, index) => ({ row, index }))
            .filter(item => (Number(item.row.totalToInvoice) || 0) > 0);
    }

    private rebuildDepositRowsToBill(): void {
        this.depositRowsToBill = (this.invoiceRows || [])
            .map((row, index) => ({ row, index }))
            .filter(item => (Number(item.row.netAmount) || 0) < 0);
    }

    async loadDetail(): Promise<void> {
        const detail = await this.partnerLiquidationsService.getPartnerLiquidationDetail(this.liquidationId);
        if(!detail) {
            return;
        }

        this.detail    = detail;
        this.paysISR   = detail.paysISR === true;
        this.statusText = this.translateStatus(detail.status);
        const rows = Array.isArray(detail.rows) ? detail.rows : [];
        const fallbackInvoices = Array.isArray(detail.invoices) ? detail.invoices : [];
        const fallbackDeposits = Array.isArray(detail.deposits) ? detail.deposits : [];
        this.invoiceRows = rows.map((row: PartnerLiquidationRow, index: number) => {
            const groupId = row.groupId || row.busId || '';
            const group = row.group || row.busName || groupId;
            const fallbackInvoice = row.invoice || fallbackInvoices[index] || { url: '', fileName: '', number: '', date: '', amount: 0 };
            const totalToInvoice = this.calculateRowTotal(row);
            const netAmount = this.calculateRowNet(row);
            const invoiceDraft: PartnerDocumentDraft = {
                url      : fallbackInvoice.url || '',
                fileName : fallbackInvoice.fileName || '',
                number   : fallbackInvoice.number || '',
                date     : fallbackInvoice.date || '',
                amount   : totalToInvoice
            };
            const fallbackDeposit = row.deposit || fallbackDeposits[index] || { url: '', fileName: '', number: '', date: '', amount: 0 };
            const depositDraft: PartnerDocumentDraft = {
                url      : fallbackDeposit.url || '',
                fileName : fallbackDeposit.fileName || '',
                number   : fallbackDeposit.number || '',
                date     : fallbackDeposit.date || '',
                amount   : Math.abs(netAmount)
            };
            return {
                busId              : row.busId || groupId,
                busName            : row.busName || row.busId || groupId,
                groupId            : groupId,
                group              : group,
                groupType          : row.groupType,
                date               : row.date || '',
                passagesAmount     : Number(row.passagesAmount) || 0,
                discounts          : Array.isArray(row.discounts) ? row.discounts : [],
                isrAmount          : Number(row.isrAmount) || 0,
                retentionIVAAmount : Number(row.retentionIVAAmount) || 0,
                concept            : row.concept || '',
                totalToInvoice     : totalToInvoice,
                netAmount          : netAmount,
                depositAmount      : Math.abs(netAmount),
                invoice            : invoiceDraft,
                deposit            : depositDraft
            };
        });
        this.revokePreviews();

        this.recalculateTotals();
        this.rebuildInvoiceRowsToBill();
        this.rebuildDepositRowsToBill();
    }

    getRowGroupLabel(row: { group?: string; groupId?: string; busName?: string; busId?: string }): string {
        return row.group || row.groupId || row.busName || row.busId || '';
    }

    async copyConcept(concept: string | undefined): Promise<void> {
        if(!concept) {
            return;
        }
        try {
            await navigator.clipboard.writeText(concept);
            this.alertService.success('Descripción copiada al portapapeles.', 'Copiado');
        } catch(error) {
            console.error('Error al copiar la descripción:', error);
            this.alertService.error('No se pudo copiar la descripción.', 'Error');
        }
    }

    recalculateTotals(): void {
        const rows = this.invoiceRows || [];
        const discountTotal = rows.reduce((sum, row) => sum + this.calculateRowDiscountTotal(row), 0);

        this.totalPassages  = rows.reduce((sum, row) => sum + (Number(row.passagesAmount) || 0), 0);
        this.totalDiscounts = discountTotal;
        this.totalToInvoice = this.roundToTwo(this.totalPassages);
        this.netAmount      = this.roundToTwo(this.totalPassages - discountTotal);
        this.totalToDeposit = this.netAmount < 0 ? Math.abs(this.netAmount) : 0;
    }

    private calculateRowDiscountTotal(row: { discounts?: PartnerLiquidationRowDiscount[]; isrAmount?: number; retentionIVAAmount?: number }): number {
        const discountsTotal = (row.discounts || []).reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
        const isrAmount = Number(row.isrAmount) || 0;
        const retentionAmount = Number(row.retentionIVAAmount) || 0;
        return this.roundToTwo(discountsTotal + isrAmount + retentionAmount);
    }

    private calculateRowTotal(row: PartnerLiquidationRow): number {
        const passages = Number(row.passagesAmount) || 0;
        return this.roundToTwo(passages);
    }

    private calculateRowNet(row: PartnerLiquidationRow): number {
        const passages = Number(row.passagesAmount) || 0;
        const discounts = this.calculateRowDiscountTotal(row);
        return this.roundToTwo(passages - discounts);
    }

    private roundToTwo(value: number): number {
        return Math.round((Number(value) || 0) * 100) / 100;
    }

    private isValidDateString(value: any): boolean {
        if(!value) {
            return false;
        }
        const date = new Date(value);
        if(isNaN(date.getTime())) {
            return false;
        }
        return date.getTime() <= this.today.getTime();
    }

    isInvoiceNumberInvalid(index: number): boolean {
        const number = this.invoiceRows[index]?.invoice?.number;
        return !number || String(number).trim() === '';
    }

    isInvoiceDateInvalid(index: number): boolean {
        const date = this.invoiceRows[index]?.invoice?.date;
        return !this.isValidDateString(date);
    }

    isDepositNumberInvalid(index: number): boolean {
        const number = this.invoiceRows[index]?.deposit?.number;
        return !number || String(number).trim() === '';
    }

    isDepositDateInvalid(index: number): boolean {
        const date = this.invoiceRows[index]?.deposit?.date;
        return !this.isValidDateString(date);
    }

    private async uploadPendingDocuments(docs: PartnerDocumentDraft[], target: 'invoices' | 'deposits', uid: string, uploaded: { path: string; doc: PartnerDocumentDraft }[]): Promise<void> {
        const pending = docs.filter(d => d.file && !d.url);

        if(pending.length === 0) {
            return;
        }

        this.uploadTarget = target;
        let completed = 0;

        for(const doc of pending) {
            const path       = `partner-liquidations/${uid}/${this.liquidationId}/${target}/${Date.now()}_${doc.fileName}`;
            const storageRef = ref(this.storage, path);
            const uploadTask = uploadBytesResumable(storageRef, doc.file as File);

            await new Promise<void>((resolve, reject) => {
                uploadTask.on('state_changed', snapshot => {
                    this.uploadProgress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                }, reject, async () => {
                    const url = await getDownloadURL(uploadTask.snapshot.ref);
                    doc.url = url;
                    doc.storagePath = path;
                    uploaded.push({ path, doc });
                    completed += 1;
                    this.uploadMessage = `Subiendo ${completed} de ${pending.length}`;
                    resolve();
                });
            });
        }

        this.uploadTarget = null;
    }

    private async rollbackUploads(uploaded: { path: string; doc: PartnerDocumentDraft }[]): Promise<void> {
        for(const item of uploaded) {
            try {
                const storageRef = ref(this.storage, item.path);
                await deleteObject(storageRef);
                item.doc.url = '';
                item.doc.storagePath = undefined;
            } catch(error) {
                console.error('Error al eliminar archivo subido tras fallo:', error);
            }
        }
    }

    private revokePreviews(): void {
        const revoke = (docs: PartnerDocumentDraft[]) => {
            docs.forEach(d => {
                if(d.previewUrl) {
                    URL.revokeObjectURL(d.previewUrl);
                    d.previewUrl = undefined;
                }
            });
        };
        revoke(this.invoiceRows.map(row => row.invoice));
        revoke(this.invoiceRows.map(row => row.deposit));
    }

    translateStatus(status: string): string {
        const map: Record<string, string> = {
            'PENDING_UPLOAD'    : 'Pendiente de documentos',
            'NOTIFICATION_FAILED': 'Notificación fallida',
            'REVIEW_IN_PROGRESS': 'En revisión',
            'REJECTED'          : 'Rechazada',
            'APPROVED'          : 'Aprobada'
        };
        return map[status] || status;
    }

    get retentionMin(): number {
        return this.detail?.retentionMinAmount || 2500;
    }

    get isDaily(): boolean {
        return this.detail?.billingMode === 'DAILY';
    }

    get isWeekly(): boolean {
        return this.detail?.billingMode === 'WEEKLY';
    }

    formatDateDisplay(value: any): string {
        const date = convertFirestoreTimestampToDate(value);
        if(!date || isNaN(date.getTime())) {
            return '';
        }
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }

    isUploadingTarget(target: 'invoices' | 'deposits'): boolean {
        return this.uploading && this.uploadTarget === target;
    }

    handleInvoiceFileInput(event: Event, index: number): void {
        if(!this.canEdit) {
            return;
        }
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files.length > 0 ? input.files[0] : null;
        if(input) {
            input.value = '';
        }
        if(!file) {
            return;
        }
        if(file.type !== 'application/pdf') {
            this.alertService.warning('Solo se permiten archivos PDF.', 'Advertencia');
            return;
        }
        const row = this.invoiceRows[index];
        if(!row) {
            return;
        }
        if(row.invoice.previewUrl) {
            URL.revokeObjectURL(row.invoice.previewUrl);
        }
        row.invoice = {
            ...row.invoice,
            url       : '',
            fileName  : file.name,
            file      : file,
            previewUrl: URL.createObjectURL(file)
        };
    }

    handleDepositFileInput(event: Event, index: number): void {
        if(!this.canEdit) {
            return;
        }
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files.length > 0 ? input.files[0] : null;
        if(input) {
            input.value = '';
        }
        if(!file) {
            return;
        }
        if(file.type !== 'application/pdf') {
            this.alertService.warning('Solo se permiten archivos PDF.', 'Advertencia');
            return;
        }
        const row = this.invoiceRows[index];
        if(!row) {
            return;
        }
        if(row.deposit.previewUrl) {
            URL.revokeObjectURL(row.deposit.previewUrl);
        }
        row.deposit = {
            ...row.deposit,
            url       : '',
            fileName  : file.name,
            file      : file,
            previewUrl: URL.createObjectURL(file)
        };
    }

    clearInvoiceFile(index: number): void {
        if(!this.canEdit) {
            return;
        }
        const row = this.invoiceRows[index];
        if(!row) {
            return;
        }
        if(row.invoice.previewUrl) {
            URL.revokeObjectURL(row.invoice.previewUrl);
        }
        row.invoice = {
            ...row.invoice,
            url      : '',
            fileName : '',
            number   : '',
            date     : '',
            file     : undefined,
            previewUrl: undefined
        };
    }

    clearDepositFile(index: number): void {
        if(!this.canEdit) {
            return;
        }
        const row = this.invoiceRows[index];
        if(!row) {
            return;
        }
        if(row.deposit.previewUrl) {
            URL.revokeObjectURL(row.deposit.previewUrl);
        }
        row.deposit = {
            ...row.deposit,
            url      : '',
            fileName : '',
            number   : '',
            date     : '',
            file     : undefined,
            previewUrl: undefined
        };
    }

    private validateInvoices(): string | null {
        if(!this.detail) {
            return 'No se pudo cargar la información de la liquidación.';
        }

        const rowsToBill = this.invoiceRowsToBill.map(item => item.row);

        if(rowsToBill.length === 0) {
            return null;
        }

        if(rowsToBill.some(row => !row.invoice.fileName || (!row.invoice.url && !row.invoice.file))) {
            return 'Debes subir una factura por cada fila.';
        }

        if(rowsToBill.some(row => !row.invoice.number || !row.invoice.date)) {
            return 'Completa número y fecha de cada factura.';
        }

        return null;
    }

    private validateDeposits(): string | null {
        const rowsToDeposit = this.depositRowsToBill.map(item => item.row);
        if(rowsToDeposit.length === 0) {
            return null;
        }

        if(rowsToDeposit.some(row => !row.deposit.fileName || (!row.deposit.url && !row.deposit.file))) {
            return 'Debes subir un comprobante por cada fila.';
        }

        if(rowsToDeposit.some(row => !row.deposit.number || !row.deposit.date)) {
            return 'Completa número y fecha de cada depósito.';
        }

        return null;
    }

    async saveDocuments(): Promise<void> {
        if(!this.detail) {
            return;
        }

        if(!this.canEdit) {
            return;
        }
        if(this.uploading) {
            this.alertService.info('Espera a que finalicen las cargas antes de enviar.', 'Carga en progreso');
            return;
        }

        this.loaderService.show();
        const uid = this.authService.authInstance.currentUser?.uid;
        if(!uid) {
            this.alertService.error('No se pudo identificar al usuario autenticado.', 'Error');
            this.loaderService.hide();
            return;
        }

        const rowsToBill = this.invoiceRowsToBill.map(item => item.row);
        const invalidInvoiceDates = rowsToBill.some(row => !this.isValidDateString(row.invoice.date));
        const depositRows = this.depositRowsToBill.map(item => item.row);
        const invalidDepositDates = depositRows.some(row => !this.isValidDateString(row.deposit.date));

        if(invalidInvoiceDates || invalidDepositDates) {
            this.alertService.warning('Verifica que las fechas de facturas y depósitos sean válidas.', 'Validación');
            this.loaderService.hide();
            return;
        }

        const invoiceError = this.validateInvoices();
        if(invoiceError) {
            this.alertService.warning(invoiceError, 'Validación');
            this.loaderService.hide();
            return;
        }

        const depositError = this.validateDeposits();
        if(depositError) {
            this.alertService.warning(depositError, 'Validación');
            this.loaderService.hide();
            return;
        }

        const uploaded: { path: string; doc: PartnerDocumentDraft }[] = [];
        this.uploading     = true;
        this.uploadMessage = 'Subiendo archivos...';
        this.uploadProgress = 0;

        try {
            const invoiceDocs = this.invoiceRows.map(row => row.invoice);
            const depositDocs = this.invoiceRows.map(row => row.deposit);
            await this.uploadPendingDocuments(invoiceDocs, 'invoices', uid, uploaded);
            await this.uploadPendingDocuments(depositDocs, 'deposits', uid, uploaded);

            const payloadInvoices = this.invoiceRows.map(row => ({
                url      : row.invoice.url,
                fileName : row.invoice.fileName,
                number   : row.invoice.number.trim(),
                date     : row.invoice.date
            }));
            const payloadDeposits = this.invoiceRows.map(row => ({
                url      : row.deposit.url,
                fileName : row.deposit.fileName,
                number   : row.deposit.number.trim(),
                date     : row.deposit.date
            }));

            const saved = await this.partnerLiquidationsService.savePartnerLiquidationDocuments(
                this.liquidationId,
                payloadInvoices,
                payloadDeposits
            );

            if(saved) {
                this.revokePreviews();
                await this.loadDetail();
            } else {
                throw new Error('No se pudo guardar la liquidación.');
            }
        } catch(error: any) {
            console.error('Error al enviar documentos:', error);
            await this.rollbackUploads(uploaded);
            this.alertService.error('Ocurrió un error al enviar los documentos. Inténtalo nuevamente.', 'Error');
        } finally {
            this.uploading      = false;
            this.uploadTarget   = null;
            this.uploadMessage  = '';
            this.uploadProgress = 0;
            this.loaderService.hide();
            if(!uploaded.length) {
                this.revokePreviews();
            }
        }
    }

    goBack(): void {
        this.router.navigate(['/liquidations']);
    }
}
